// Detects column-level conflicts between two participants prior to merge.
//
// Phase C commit 2: when both keep and drop have non-empty, normalized-
// different values on the same column, the coordinator must pick which
// value survives. This util reads participants_v (so any sensitive-field
// masking is applied per RLS — coordinators see unmasked, field workers
// see masked, but field workers can't reach the merge UI anyway) and
// returns a list of conflict descriptors.
//
// Returns { conflicts, keep, drop } where each conflict is
//   { field: <column>, keepValue: <raw>, dropValue: <raw> }
//
// `keep` and `drop` are the full row objects, useful for showing context
// in the resolution modal.

import { normalizeEmail } from './normalizeEmail'

// Columns we surface for resolution. These are user-facing demographic +
// contact + identity fields tracked in fieldLabels.js. birth_year is
// excluded — it's a derived column maintained by mig 32's trigger from
// birth_date, so a "conflict" on birth_year alone is spurious.
export const COMPARABLE_FIELDS = [
  'first_name',
  'last_name',
  'phone',
  'email',
  'address',
  'birth_date',
  'approximate_age',
  'bahai_status',
  'locality',
  'sex',
  'age_category',
  'private_notes',
]

const normalizeForCompare = (field, val) => {
  if (val == null) return null
  const s = String(val)
  if (s.trim() === '') return null
  if (field === 'phone') {
    const d = s.replace(/\D/g, '')
    return d || null
  }
  if (field === 'email') {
    return normalizeEmail(s)
  }
  // address / private_notes: trim + lowercase compare so trivial
  // whitespace/casing drift doesn't surface as a conflict.
  if (field === 'address' || field === 'private_notes' ||
      field === 'first_name' || field === 'last_name' || field === 'locality') {
    return s.trim().toLowerCase()
  }
  // birth_date / approximate_age / bahai_status / sex / age_category:
  // straight string comparison after trim.
  return s.trim()
}

export async function detectMergeConflicts(supabase, keepId, dropId) {
  const { data, error } = await supabase
    .from('participants_v')
    .select(
      'id, first_name, last_name, phone, email, address, birth_date, ' +
      'approximate_age, bahai_status, locality, sex, age_category, private_notes'
    )
    .in('id', [keepId, dropId])

  if (error) {
    throw new Error('Failed to load participant data for conflict check: ' + error.message)
  }

  const rows = data || []
  const keep = rows.find(r => r.id === keepId)
  const drop = rows.find(r => r.id === dropId)
  if (!keep || !drop) {
    throw new Error('Could not load both participant records for conflict check')
  }

  const conflicts = []
  for (const f of COMPARABLE_FIELDS) {
    const kn = normalizeForCompare(f, keep[f])
    const dn = normalizeForCompare(f, drop[f])
    if (kn != null && dn != null && kn !== dn) {
      conflicts.push({
        field: f,
        keepValue: keep[f],
        dropValue: drop[f],
      })
    }
  }
  return { conflicts, keep, drop }
}
